import { useState } from "react";
import { motion } from "framer-motion";
import { Mic, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";

const PRICING_PLANS = [
  {
    id: "starter",
    name: "Starter Agent",
    subtitle: "For Solopreneurs & Creators",
    monthlyPrice: 99,
    yearlyPrice: 79,
    features: [
      { emoji: "🎙️", text: "Voice Commands: 2 Languages", included: true },
      { emoji: "📱", text: "1 Social Platform", included: true },
      { emoji: "🖼️", text: "15 AI Images / Mo", included: true },
      { emoji: "✍️", text: "15 Smart Captions", included: true },
      { emoji: "❌", text: "No Video", included: false },
      { emoji: "📅", text: "Manual Approval Required", included: true }
    ],
    cta: "Start 7-Day Preview",
    popular: false,
    handsOff: false
  },
  {
    id: "pro",
    name: "Pro Agent",
    subtitle: "For Growing Companies",
    monthlyPrice: 499,
    yearlyPrice: 399,
    features: [
      { emoji: "🎙️", text: "Voice Commands: All Languages", included: true },
      { emoji: "📱", text: "4 Social Platforms", included: true },
      { emoji: "🖼️", text: "45 AI Images / Mo", included: true },
      { emoji: "✍️", text: "45 Smart Captions", included: true },
      { emoji: "🎬", text: "8 AI Videos / Mo", included: true },
      { emoji: "🤖", text: "100% Autonomous Mode", included: true }
    ],
    cta: "Start 7-Day Preview",
    popular: true,
    handsOff: true
  },
  {
    id: "enterprise",
    name: "Enterprise Agent",
    subtitle: "For Global Brands & Agencies",
    monthlyPrice: 1499,
    yearlyPrice: 1199,
    features: [
      { emoji: "🎙️", text: "Voice Commands: 24/7 Priority", included: true },
      { emoji: "📱", text: "Unlimited Platforms", included: true },
      { emoji: "🖼️", text: "Unlimited AI Images", included: true },
      { emoji: "✍️", text: "Unlimited Captions", included: true },
      { emoji: "🎬", text: "25 AI Videos / Mo", included: true },
      { emoji: "🤖", text: "Autonomous + Competitor IQ", included: true }
    ],
    cta: "Contact Sales",
    popular: false,
    handsOff: true,
    extra: "+ $2,000 Knowledge Base Training"
  }
];

/**
 * PricingSection Component - WITH ANIMATED LINES AND SPEAK TO AGENT BUTTON
 */
export default function PricingSection({ onContactSales, onOpenVoiceChat }) {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated Lines Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent animate-pulse" />
        <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Voice-Activated Pricing
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4">
            Choose Your <span className="gradient-text">Autonomous Marketing Agent</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Everything is voice-activated. No more dashboards or complex settings.
          </p>
        </motion.div>

        {/* Active Agents Counter with Animated Lines */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            {/* Three animated horizontal lines */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-48 flex flex-col gap-1">
              <div className="h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse rounded-full" />
              <div className="h-0.5 bg-gradient-to-r from-transparent via-primary/60 to-transparent animate-pulse rounded-full" style={{ animationDelay: '0.3s' }} />
              <div className="h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent animate-pulse rounded-full" style={{ animationDelay: '0.6s' }} />
            </div>
            
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-500/10 border border-green-500/30 mt-4">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-400">
                <strong>1,245</strong> Agents are currently creating content
              </span>
            </div>
          </div>
        </motion.div>

        {/* Annual Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={!annual ? 'font-semibold' : 'text-muted-foreground'}>Monthly</span>
          <button
            onClick={() => setAnnual(!annual)}
            className={`w-14 h-7 rounded-full p-1 transition-colors ${annual ? 'bg-primary' : 'bg-white/20'}`}
            data-testid="annual-toggle"
          >
            <motion.div
              animate={{ x: annual ? 26 : 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="w-5 h-5 bg-white rounded-full shadow"
            />
          </button>
          <span className={annual ? 'font-semibold' : 'text-muted-foreground'}>Annual</span>
          {annual && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              2 months FREE
            </Badge>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {PRICING_PLANS.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative glass-card rounded-2xl p-6 border transition-all ${
                plan.popular 
                  ? 'border-primary shadow-lg shadow-primary/20' 
                  : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`pricing-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-white">Most Popular</Badge>
                </div>
              )}
              
              {plan.handsOff && (
                <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                  Hands-Off Certified
                </Badge>
              )}

              <div className="mb-6">
                <h3 className="text-2xl font-bold">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.subtitle}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">
                    ${annual ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="text-lg">{feature.emoji}</span>
                    <span className={!feature.included ? 'text-muted-foreground' : ''}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.extra && (
                <p className="text-sm text-muted-foreground mb-4">{plan.extra}</p>
              )}

              {plan.id === 'enterprise' ? (
                <Button className="w-full" variant="outline" onClick={onContactSales}>
                  {plan.cta}
                </Button>
              ) : (
                <Link to="/register" className="block">
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA - Speak to the Agent button */}
        <div className="text-center mt-12">
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary/90"
            onClick={onOpenVoiceChat}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Speak to the Agent
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            7-day preview • No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}
